// import React, { useState, useEffect } from "react";
// import { useDropzone } from "react-dropzone";
// import Swal from "sweetalert2";
// import { Icon } from "@iconify/react";
// const FileUpload = ({
//   label,
//   multiple = false,
//   maxFiles,
//   images = [], // ← new prop: full list of images
//   onImagesChange, // ← new callback: returns updated full list
// }) => {
//   const { getRootProps, getInputProps, isDragActive } = useDropzone({
//     multiple,
//     accept: { "image/*": [] },
//     onDrop: (acceptedFiles) => {
//       const newImages = acceptedFiles.map((file) => ({
//         file,
//         preview: URL.createObjectURL(file),
//         isExisting: false,
//       }));

//       let updated = [...images, ...newImages];

//       if (maxFiles && updated.length > maxFiles) {
//         updated = updated.slice(0, maxFiles);
//         Swal.fire(
//           "Limit reached",
//           `Maximum ${maxFiles} files allowed`,
//           "warning"
//         );
//       }

//       onImagesChange(updated);
//     },
//   });

//   const removeImage = (index) => {
//     const updated = images.filter((_, i) => i !== index);
//     onImagesChange(updated);
//   };

//   // Cleanup previews on unmount
//   useEffect(() => {
//     return () => {
//       images.forEach((img) => {
//         if (!img.isExisting && img.preview) {
//           URL.revokeObjectURL(img.preview);
//         }
//       });
//     };
//   }, [images]);

//   return (
//     <div className="mb-3">
//       <label className="form-label fw-semibold">{label}</label>

//       <div
//         {...getRootProps()}
//         className={`border rounded p-5 text-center cursor-pointer ${
//           isDragActive ? "border-primary bg-light" : "border-secondary"
//         }`}
//         style={{
//           minHeight: "150px",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           flexDirection: "column",
//         }}>
//         <input {...getInputProps()} />
//         <Icon
//           icon="mdi:cloud-upload-outline"
//           width="40"
//           className="text-muted mb-2"
//         />
//         <p className="mb-0 text-muted">
//           {isDragActive
//             ? "Drop files here..."
//             : "Drop files here or click to upload"}
//         </p>
//       </div>

//       {images.length > 0 && (
//         <div className="d-flex gap-3 flex-wrap mt-3">
//           {images.map((img, idx) => (
//             <div
//               key={idx}
//               className="position-relative border rounded p-2 text-center"
//               style={{ width: 120 }}>
//               <button
//                 type="button"
//                 className="btn-close position-absolute top-0 end-0 m-1"
//                 onClick={() => removeImage(idx)}
//                 style={{ fontSize: "0.7rem" }}
//               />
//               <img
//                 src={img.preview}
//                 alt="preview"
//                 style={{
//                   width: "100%",
//                   height: 80,
//                   objectFit: "cover",
//                   borderRadius: "8px",
//                 }}
//               />
//               <small className="d-block text-truncate">
//                 {img.isExisting ? "existing" : img.file?.name || "new"}
//               </small>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// export default FileUpload;
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";

const FileUpload = ({
  label,
  multiple = false,
  maxFiles,
  accept = { "image/*": [] },
  fileType = "image",
  images: imagesProp, // full list of images
  onImagesChange, // returns updated full list
}) => {
  const images = useMemo(() => imagesProp || [], [imagesProp]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const imageRefs = useRef([]);

  // Handle file drop (same as before)
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    accept,
    onDrop: (acceptedFiles) => {
      const newImages = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        isExisting: false,
      }));

      let updated = multiple ? [...images, ...newImages] : newImages;

      if (maxFiles && updated.length > maxFiles) {
        updated = updated.slice(0, maxFiles);
        Swal.fire(
          "Limit reached",
          `Maximum ${maxFiles} files allowed`,
          "warning",
        );
      }

      onImagesChange(updated);
    },
  });

  const isVideoFile = (item) => {
    if (fileType === "video" || item?.file?.type?.startsWith("video/")) {
      return true;
    }

    return /\.(mp4|webm|ogg|mov|m4v)(?:\?|#|$)/i.test(item?.preview || "");
  };

  // Reorder images when drag ends
  const handleDragEnd = useCallback(
    (dragIndex, dropIndex) => {
      if (dragIndex === dropIndex) return;

      const updated = [...images];
      const [draggedItem] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, draggedItem);

      onImagesChange(updated);
      setDraggedIndex(null);
    },
    [images, onImagesChange],
  );

  // Drag handlers
  const handleDragStart = useCallback((index) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();
      if (draggedIndex !== null) {
        handleDragEnd(draggedIndex, dropIndex);
      }
    },
    [draggedIndex, handleDragEnd],
  );

  // Remove image
  const removeImage = useCallback(
    (index) => {
      const updated = images.filter((_, i) => i !== index);
      onImagesChange(updated);
    },
    [images, onImagesChange],
  );

  // Cleanup previews
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (!img.isExisting && img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, [images]);

  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>

      {/* Upload Dropzone */}
      <div
        {...getRootProps()}
        className={`border rounded p-5 text-center cursor-pointer ${
          isDragActive ? "border-primary bg-light" : "border-secondary"
        }`}
        style={{
          minHeight: "150px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}>
        <input {...getInputProps()} />
        <Icon
          icon="mdi:cloud-upload-outline"
          width="40"
          className="text-muted mb-2"
        />
        <p className="mb-0 text-muted">
          {isDragActive
            ? "Drop files here..."
            : "Drop files here or click to upload"}
        </p>
      </div>

      {/* Images Grid - NOW DRAGGABLE */}
      {images.length > 0 && (
        <div className="d-flex gap-3 flex-wrap mt-3">
          {images.map((img, idx) => (
            <div
              key={idx}
              ref={(el) => (imageRefs.current[idx] = el)}
              className={`position-relative border rounded p-2 text-center draggable-item ${
                draggedIndex === idx
                  ? "opacity-50 border-primary shadow-lg scale-105"
                  : draggedIndex !== null
                    ? "drag-hover"
                    : ""
              }`}
              style={{
                width: 120,
                cursor: draggedIndex !== null ? "grab" : "grab",
                transition: "all 0.2s ease",
                transform: draggedIndex === idx ? "scale(1.05)" : "scale(1)",
              }}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={() => setDraggedIndex(null)}>
              {/* Drag Handle Icon */}
              <div
                className="position-absolute top-0 start-0 m-1 bg-dark bg-opacity-75 text-white rounded p-1"
                style={{ fontSize: "0.8rem", zIndex: 10 }}>
                <Icon icon="mdi:drag-vertical" />
              </div>

              {/* Remove Button */}
              <button
                type="button"
                className="btn-close position-absolute top-0 end-0 m-1"
                onClick={() => removeImage(idx)}
                style={{ fontSize: "0.7rem", zIndex: 10 }}
              />

              {/* File Preview */}
              {isVideoFile(img) ? (
                <video
                  src={img.preview}
                  controls
                  muted
                  style={{
                    width: "100%",
                    height: 80,
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <img
                  src={img.preview}
                  alt="preview"
                  style={{
                    width: "100%",
                    height: 80,
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              )}

              {/* Label */}
              <small className="d-block text-truncate mt-1">
                {img.isExisting ? "existing" : img.file?.name || "new"}
              </small>

              {/* Order Indicator */}
              <small
                className="position-absolute bottom-0 start-50 translate-middle-x bg-primary text-white rounded px-1 py-0 fw-bold"
                style={{ fontSize: "0.65rem", zIndex: 5 }}>
                {idx + 1}
              </small>
            </div>
          ))}
        </div>
      )}

      {/* Hint for reordering */}
      {images.length > 1 && (
        <small className="text-muted d-block mt-2">
          <Icon icon="mdi:drag-vertical" className="me-1" /> Drag files to
          reorder
        </small>
      )}

      <style jsx>{`
        .draggable-item {
          user-select: none;
        }
        .draggable-item.drag-hover {
          border-color: #0d6efd !important;
          transform: scale(1.02);
        }
        .draggable-item:active {
          cursor: grabbing !important;
        }
        @media (prefers-reduced-motion: no-preference) {
          .draggable-item {
            scroll-snap-type: x mandatory;
          }
        }
      `}</style>
    </div>
  );
};

export default FileUpload;
